if (!customElements.get('nomosu-testimonials')) {
  customElements.define('nomosu-testimonials', class extends HTMLElement {
    connectedCallback() {
      if (this.destroy) return;
      const section = this;
      const viewport = section.querySelector('.yt-viewport');
      const track = section.querySelector('.yt-track');
      const prevBtn = section.querySelector('.yt-prev');
      const nextBtn = section.querySelector('.yt-next');
      const nav = section.querySelector('.yt-nav');
      const status = section.querySelector('.yt-status');

      const appKey = (section.dataset.appKey || '').trim();
      const productId = (section.dataset.productId || '').trim();
      const autoplaySeconds = Math.max(2, parseInt(section.dataset.autoplaySeconds, 10) || 4);
      const maxReviews = Math.max(1, parseInt(section.dataset.maxReviews, 10) || 20);
      const fiveStarOnly = String(section.dataset.fiveStarOnly) === 'true';

      let reviews = [];
      let currentIndex = 0;
      let cloneCount = 0;
      let autoTimer = null;
      let isAnimating = false;
      let resizeTimer = null;
      let touchStartX = 0;
      let touchDeltaX = 0;
      let abortController = null;
      let selected = false;
      let hovered = false;
      let destroyed = false;
      const listeners = new AbortController();
      const pendingTimeouts = new Set();
      const later = (fn, ms) => { const id = window.setTimeout(() => { pendingTimeouts.delete(id); if (!destroyed) fn(); }, ms); pendingTimeouts.add(id); return id; };
      const on = (target, event, callback, options = {}) => target.addEventListener(event, callback, { ...options, signal: listeners.signal });
      this.destroy = () => {
        destroyed = true; stopAuto(); listeners.abort(); abortController?.abort();
        pendingTimeouts.forEach(id => clearTimeout(id)); pendingTimeouts.clear(); clearTimeout(resizeTimer);
      };

      function getVisibleCount() {
        return Math.min(reviews.length || 1, window.innerWidth <= 989 ? 1 : 3);
      }

      function normalizeText(value) {
        if (!value) return '';
        return String(value)
          .replace(/\s+/g, ' ')
          .replace(/^["“”]+|["“”]+$/g, '')
          .trim();
      }

      function escapeHtml(str) {
        return String(str || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      function buildStars(rating) {
        const count = Math.max(1, Math.min(5, parseInt(rating, 10) || 5));
        return section.querySelector('[data-review-star]').innerHTML.trim().repeat(count);
      }

      function formatReviewText(value) {
        const icons = {
          '😄': '<svg class="yt-inline-emoji" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-label="😄" focusable="false"><circle cx="12" cy="12" r="10" fill="#FFCC4D"/><path d="M5.5 9.5q2-3 4 0m5 0q2-3 4 0" fill="none" stroke="#664500" stroke-width="1.8" stroke-linecap="round"/><path d="M6.5 13h11a5.5 5.5 0 0 1-11 0Z" fill="#664500"/><path d="M7.2 13.5h9.6l-.8 2H8Z" fill="#FFF"/><path d="M9 18a4.5 4.5 0 0 1 6 0 5.5 5.5 0 0 1-6 0Z" fill="#F4778A"/></svg>',
          '😍': '<svg class="yt-inline-emoji" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-label="😍" focusable="false"><circle cx="12" cy="12" r="10" fill="#FFCC4D"/><path d="M7 11 3.8 7.9C1.9 5.9 4.9 3.8 7 6c2.1-2.2 5.1-.1 3.2 1.9L7 11Zm10 0-3.2-3.1C11.9 5.9 14.9 3.8 17 6c2.1-2.2 5.1-.1 3.2 1.9L17 11Z" fill="#E53935"/><path d="M7 14h10a5 5 0 0 1-10 0Z" fill="#664500"/><path d="M9 17.8a4 4 0 0 1 6 0 5 5 0 0 1-6 0Z" fill="#F4778A"/></svg>',
          '🍫': '<svg class="yt-inline-emoji" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-label="🍫" focusable="false"><g transform="rotate(-12 12 12)"><rect x="5" y="2" width="14" height="20" rx="1" fill="#56321F"/><path d="M7 4h4v4H7Zm6 0h4v4h-4ZM7 10h4v4H7Zm6 0h4v4h-4Z" fill="#8B5738"/><path d="m5 13 7 2 7-2v9H5Z" fill="#C73B51"/><path d="m5 13 7 2 7-2-2 4H7Z" fill="#DFE4E7"/></g></svg>',
          '💕': '<svg class="yt-inline-emoji" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-label="💕" focusable="false"><path d="m8 22-6-5.7C-2 12.3 4 7.4 8 11.3c4-3.9 10 1 6 5L8 22Z" fill="#F06AA8"/><path d="m17 12-4.5-4.3C9.5 4.7 14 1 17 3.9c3-2.9 7.5.8 4.5 3.8L17 12Z" fill="#E74491"/></svg>'
        };
        return escapeHtml(value).replace(/😍|🍫|💕|😄/gu, function(emoji) {
          return icons[emoji];
        });
      }

      function setStatus(message, show) {
        status.textContent = message || '';
        status.hidden = !show;
      }

      function setNavState(enabled) {
        const disabled = !enabled;
        prevBtn.disabled = disabled;
        nextBtn.disabled = disabled;
        nav.hidden = disabled;
      }

      function dedupeReviews(items) {
        const seen = new Set();
        return items.filter(function(item) {
          const key = ((item.author || '') + '|' + (item.text || '')).toLowerCase();
          if (!item.text || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }

      function mapApiReview(item) {
        const score = Number(item && item.score);
        const content = normalizeText(item && item.content ? item.content : '');
        const name = normalizeText(
          item && item.user && item.user.display_name
            ? item.user.display_name
            : item && item.name
              ? item.name
              : 'Anonymous'
        );

        return {
          text: content,
          author: name || 'Anonymous',
          rating: Number.isFinite(score) ? score : 0,
          createdAt: item && item.created_at ? item.created_at : ''
        };
      }

      async function fetchJson(url, timeoutMs) {
        if (abortController) {
          abortController.abort();
        }

        abortController = new AbortController();
        const timeoutId = later(function() {
          abortController.abort();
        }, timeoutMs);

        try {
          const response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            signal: abortController.signal,
            headers: {
              'Accept': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Request failed with status ' + response.status);
          }

          return await response.json();
        } finally {
          window.clearTimeout(timeoutId);
        }
      }

      async function fetchAllReviews() {
        if (!appKey) {
          throw new Error('Missing Yotpo app key.');
        }

        if (!productId) {
          throw new Error('Missing Yotpo product/site review identifier.');
        }

        const collected = [];
        const perPage = 150;
        let page = 1;
        let totalPages = 1;

        while (page <= totalPages && collected.length < maxReviews * 3) {
          const url =
            'https://api-cdn.yotpo.com/v1/widget/' +
            encodeURIComponent(appKey) +
            '/products/' +
            encodeURIComponent(productId) +
            '/reviews.json?page=' +
            encodeURIComponent(page) +
            '&per_page=' +
            encodeURIComponent(perPage);

          const data = await fetchJson(url, 12000);
          const response = data && data.response ? data.response : {};
          const items = Array.isArray(response.reviews) ? response.reviews : [];
          const pagination = response.pagination || {};

          items.forEach(function(item) {
            collected.push(mapApiReview(item));
          });

          const total = parseInt(pagination.total, 10);
          const currentPerPage = parseInt(pagination.per_page, 10) || perPage;

          if (Number.isFinite(total) && total > 0 && currentPerPage > 0) {
            totalPages = Math.max(1, Math.ceil(total / currentPerPage));
          } else if (!items.length) {
            break;
          } else {
            totalPages = page;
          }

          page += 1;
        }

        let normalized = dedupeReviews(collected).filter(function(review) {
          if (!review.text) return false;
          if (fiveStarOnly) return review.rating === 5;
          return review.rating > 0;
        });

        normalized = normalized.slice(0, maxReviews);

        return normalized;
      }

      function createCard(review, realIndex) {
        const card = document.createElement('article');
        card.className = 'yt-card';
        card.setAttribute('data-real-index', String(realIndex));
        card.setAttribute('role', 'group');
        card.setAttribute('aria-label', 'Testimonial');

        card.innerHTML = ''
          + '<div class="yt-stars" role="img" aria-label="' + escapeHtml(review.rating) + ' star review">' + buildStars(review.rating) + '</div>'
          + '<p class="yt-quote">“' + formatReviewText(review.text) + '”</p>'
          + '<p class="yt-author">– ' + escapeHtml(review.author) + '</p>';

        return card;
      }

      function buildTrackItems() {
        track.innerHTML = '';

        if (!reviews.length) {
          setStatus(section.dataset.emptyText, true);
          setNavState(false);
          return;
        }

        setStatus('', false);
        setNavState(reviews.length > 1);

        cloneCount = Math.min(getVisibleCount(), reviews.length);

        const headClones = reviews.slice(0, cloneCount);
        const tailClones = reviews.slice(-cloneCount);

        const combined = tailClones
          .map(function(item, index) {
            return { review: item, realIndex: reviews.length - cloneCount + index };
          })
          .concat(
            reviews.map(function(item, index) {
              return { review: item, realIndex: index };
            })
          )
          .concat(
            headClones.map(function(item, index) {
              return { review: item, realIndex: index };
            })
          );

        combined.forEach(function(entry) {
          track.appendChild(createCard(entry.review, entry.realIndex));
        });
      }

      function centerToIndex(animated) {
        const cards = track.querySelectorAll('.yt-card');
        if (!cards.length) return;

        const visible = getVisibleCount();
        const startIndex = cloneCount + currentIndex;
        const first = cards[startIndex];
        const last = cards[startIndex + visible - 1];

        if (!first || !last) return;

        track.style.transition = animated ? 'transform 0.55s ease' : 'none';

        const groupCenter = (first.offsetLeft + last.offsetLeft + last.offsetWidth) / 2;
        const viewportCenter = viewport.clientWidth / 2;
        const offset = groupCenter - viewportCenter;

        track.style.transform = 'translate3d(' + (-offset) + 'px,0,0)';

        cards.forEach(function(card) {
          card.classList.remove('is-active');
        });

        const middle = cards[startIndex + Math.floor(visible / 2)];
        if (middle) middle.classList.add('is-active');
      }

      function rebuild() {
        stopAuto();

        if (!reviews.length) {
          buildTrackItems();
          return;
        }

        currentIndex = ((currentIndex % reviews.length) + reviews.length) % reviews.length;
        buildTrackItems();

        requestAnimationFrame(function() {
          if (destroyed) return;
          centerToIndex(false);
          startAuto();
        });
      }

      function next() {
        if (isAnimating || reviews.length <= 1) return;

        isAnimating = true;
        currentIndex += 1;
        centerToIndex(true);

        later(function() {
          if (currentIndex >= reviews.length) {
            currentIndex = 0;
            centerToIndex(false);
          }
          isAnimating = false;
        }, 560);
      }

      function prev() {
        if (isAnimating || reviews.length <= 1) return;

        isAnimating = true;
        currentIndex -= 1;
        centerToIndex(true);

        later(function() {
          if (currentIndex < 0) {
            currentIndex = reviews.length - 1;
            centerToIndex(false);
          }
          isAnimating = false;
        }, 560);
      }

      function startAuto() {
        if (destroyed || selected || hovered || section.contains(document.activeElement) || document.hidden || matchMedia('(prefers-reduced-motion: reduce)').matches || reviews.length <= 1) return;
        stopAuto();
        autoTimer = window.setInterval(next, autoplaySeconds * 1000);
      }

      function stopAuto() {
        if (autoTimer) {
          window.clearInterval(autoTimer);
          autoTimer = null;
        }
      }

      function restartAuto() {
        stopAuto();
        startAuto();
      }

      function attachEvents() {
        on(prevBtn, 'click', () => { prev(); restartAuto(); });
        on(nextBtn, 'click', () => { next(); restartAuto(); });
        on(section, 'mouseenter', () => { hovered = true; stopAuto(); });
        on(section, 'mouseleave', () => { hovered = false; startAuto(); });
        on(section, 'focusin', stopAuto);
        on(section, 'focusout', () => queueMicrotask(startAuto));
        on(document, 'visibilitychange', () => { stopAuto(); startAuto(); });
        on(document, 'shopify:section:select', event => { if (event.detail?.sectionId === section.dataset.sectionId) { selected = true; stopAuto(); } });
        on(document, 'shopify:section:deselect', event => { if (event.detail?.sectionId === section.dataset.sectionId) { selected = false; startAuto(); } });
        on(viewport, 'touchstart', event => { if (!event.touches.length) return; touchStartX = event.touches[0].clientX; touchDeltaX = 0; stopAuto(); }, { passive: true });
        on(viewport, 'touchmove', event => { if (event.touches.length) touchDeltaX = event.touches[0].clientX - touchStartX; }, { passive: true });
        on(viewport, 'touchend', () => { if (Math.abs(touchDeltaX) > 40) { touchDeltaX < 0 ? next() : prev(); } restartAuto(); });
        on(window, 'resize', () => { clearTimeout(resizeTimer); resizeTimer = later(rebuild, 120); });
      }

      async function init() {
        attachEvents();
        setStatus(section.dataset.loadingText, true);
        setNavState(false);

        try {
          reviews = await fetchAllReviews();
          if (destroyed) return;

          if (!reviews.length) {
            setStatus(section.dataset.emptyText, true);
            setNavState(false);
            track.innerHTML = '';
            return;
          }

          setStatus('', false);
          rebuild();
        } catch (error) {
          if (destroyed) return;
          track.innerHTML = '';
          setNavState(false);
          setStatus(section.dataset.errorText, true);
          if (window.console && typeof window.console.error === 'function') {
            console.error('[Yotpo Testimonials ' + section.id + ']', error);
          }
        }
      }

      init();
        }
    disconnectedCallback() { this.destroy?.(); this.destroy = null; }
  });
}
